import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosResponse } from 'axios';
import { handleApiErrors } from 'helpers';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SkillAndCertification } from 'types/interfaces';
import { RefInterface } from 'types/props';

import { ApiResponse } from 'components/services/interfaces';
import { AttainmentSchema } from 'constants/schemas';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

// todo переименовать skillsAndCertification... в attainment... согласно бэкенду
interface SkillsAndCertificationsCardProps {
  skillsAndCertificationProps: SkillAndCertification;
  closeDialog?: () => void;
  onAdd?: (
    updatedData: SkillAndCertification
  ) => Promise<AxiosResponse<ApiResponse<SkillAndCertification>, any>>;
  onUpdate?: (
    id: number,
    updatedData: SkillAndCertification
  ) => Promise<AxiosResponse<ApiResponse<SkillAndCertification>, any>>;
  onDelete: (id: string | number) => void;
}

const SkillsAndCertificationsCard = React.forwardRef<RefInterface, SkillsAndCertificationsCardProps>(
  (
    {
      closeDialog,
      onAdd,
      onDelete,
      onUpdate,
      skillsAndCertificationProps
    }: SkillsAndCertificationsCardProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
      watch,
      setError,
    } = useForm({
      resolver: zodResolver(AttainmentSchema),
      defaultValues: skillsAndCertificationProps
    });

    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const duration = watch('duration');
    const id = watch('id');

    const toast = useRef<Toast>(null);

    const onSubmit: SubmitHandler<SkillAndCertification> = useCallback(
      async (data) => {
        const newData = { ...data };
        const dataId = data.id;
        let result;
        const isIdExist =
          data && dataId !== undefined && dataId !== null && dataId !== 0;
        if (onUpdate && isIdExist) {
          result = await onUpdate(dataId, newData);
        }
        if (onAdd && !isIdExist) {
          newData.id = 0;
          result = await onAdd(newData);
        }

        if (result?.data.isSuccess) {
          closeDialog?.();
        } else {
          handleApiErrors({
            error: result?.data.error,
            setError,
            fieldMapping: {
              SameName: {
                field: 'name',
                message:
                  'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название.'
              },
              SameShortName: {
                field: 'shortName',
                message:
                  'Запись с таким коротким названием уже есть в справочнике. Пожалуйста, укажите уникальное короткое название.'
              }
            }
          });

          if (!result?.data.error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Не удалось сохранить вид отсутствия'
            });
          }
        }
      },
      [closeDialog, onAdd, onUpdate, setError]
    );

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleCancel = () => {
    
      if (isDirty) {
        setShowUnsavedDialog(true);
      } else {
        closeDialog?.();
      }
    };

    const handleDeleteConfirmation = async () => {
      if (onDelete) {
        await onDelete(id);
        closeDialog?.();
      }
      setShowDeleteDialog(false);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    return (
      <div className="dialog-tabless-card dialog-tabless-card--fixed-footer closing-code-card__roles">
        <div className="gap-3 pb-0">
          <Divider />
          <Toast ref={toast} />
          <div className="taskClosingCode-card__column">
            <div className="flex flex-column">
              {/* Поле: Полное название */}
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="name" className="col p-0">
                    Полное название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      id="name"
                      className="user-card__input w-full"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>

              {/* Поле: Короткое название */}
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="shortName" className="col p-0">
                    Короткое название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      id="shortName"
                      className="user-card__input w-full"
                      {...register('shortName')}
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    errors.shortName ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.shortName ? errors.shortName.message : ' '}
                </p>
              </div>

              {/* Поле: Описание */}
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="description" className="col p-0">
                    Описание:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      id="description"
                      className={`user-card__input w-full ${
                        errors.description ? 'p-invalid' : ''
                      }`}
                      {...register('description')}
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    errors.description ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.description ? errors.description.message : ' '}
                </p>
              </div>

              {/* Переключатель: Имеет период действия */}
              <div className="flex flex-row align-items-center gap-3 w-full">
                <InputSwitch
                  checked={duration}
                  {...register('duration')}
                />
                <label
                  className="col-fixed fixed-labels"
                  htmlFor="duration"
                >
                  Имеет период действия
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              type="submit"
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={handleDelete}
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={handleCancel}
            />
          </div>
        </div>
        <ConfirmationDialog
          visible={showUnsavedDialog}
          onCancel={() => setShowUnsavedDialog(false)}
          onContinue={closeDialog!}
          message="Есть несохранённые изменения. Вы хотите продолжить без сохранения?"
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="навык"
        />
      </div>
    );
  }
);

SkillsAndCertificationsCard.displayName = 'SkillsAndCertificationsCard';

export default SkillsAndCertificationsCard;
